<template>
  <div>
    <!-- Photo Gallery -->
    <v-row v-if="photos.length > 0" class="mb-3">
      <v-col
        v-for="(photo, index) in photos"
        :key="photo.id || photo.item_photo_url"
        cols="6"
        md="4"
        class="d-flex child-flex"
      >
        <v-card class="photo-card" outlined>
          <v-img
            :src="photo.item_photo_url"
            lazy-src="@/assets/jmbcircle.png"
            aspect-ratio="1"
            class="grey lighten-2"
          >
            <div v-if="!readonly" class="photo-overlay d-flex justify-end pa-1">
              <v-btn
                icon
                x-small
                color="error"
                class="photo-action-btn"
                @click="confirmDeletePhoto(photo, index)"
                :loading="deletingPhotoId === (photo.id || index)"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </div>
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular
                  indeterminate
                  color="grey lighten-5"
                ></v-progress-circular>
              </v-row>
            </template>
          </v-img>
          <v-card-text
            class="pa-1 text-center caption grey--text text-truncate"
          >
            {{ photo.photo_name || "Photo " + (index + 1) }}
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-alert v-else type="info" dense outlined class="mb-3">
      No photos uploaded yet.{{ readonly ? "" : " Add photos below." }}
    </v-alert>

    <!-- Upload Controls -->
    <div v-if="!readonly" class="d-flex align-center">
      <v-btn
        small
        color="primary"
        outlined
        @click="triggerFileInput"
        :loading="uploading"
      >
        <v-icon left small>mdi-camera-plus</v-icon>
        Add Photo
      </v-btn>
      <input
        ref="fileInput"
        type="file"
        accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
        multiple
        style="display: none"
        @change="onFilesSelected"
      />
      <span class="caption grey--text ml-3">
        JPEG, PNG, GIF, WebP (max 5MB each)
      </span>
    </div>

    <!-- Cropper Dialog -->
    <v-dialog v-model="cropperDialog" persistent max-width="700">
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-crop</v-icon>
          Crop Photo
          <v-spacer></v-spacer>
          <span class="caption"
            >{{ currentFileIndex + 1 }} / {{ filesToProcess.length }}</span
          >
        </v-card-title>
        <v-card-text class="pa-4">
          <div
            class="cropper-container"
            style="max-height: 450px; overflow: hidden"
          >
            <vue-cropper
              ref="cropper"
              :src="cropperImageSrc"
              :aspect-ratio="selectedAspectRatio"
              :view-mode="1"
              :auto-crop-area="0.8"
              :background="true"
              :rotatable="true"
              :scalable="true"
              :zoomable="true"
              :movable="true"
              :guides="true"
              :center="true"
              :highlight="true"
              :crop-box-movable="true"
              :crop-box-resizable="true"
              style="max-height: 400px"
            ></vue-cropper>
          </div>

          <!-- Crop Controls -->
          <div class="d-flex align-center justify-space-between mt-3">
            <!-- Aspect Ratio -->
            <div class="d-flex align-center">
              <span class="caption mr-2">Ratio:</span>
              <v-chip-group v-model="aspectRatioIndex" mandatory>
                <v-chip
                  v-for="(ratio, i) in aspectRatios"
                  :key="i"
                  small
                  :color="aspectRatioIndex === i ? 'primary' : ''"
                  :dark="aspectRatioIndex === i"
                  @click="setAspectRatio(ratio.value)"
                >
                  {{ ratio.label }}
                </v-chip>
              </v-chip-group>
            </div>

            <!-- Rotation -->
            <div class="d-flex align-center">
              <v-btn icon small @click="rotate(-90)">
                <v-icon small>mdi-rotate-left</v-icon>
              </v-btn>
              <v-btn icon small @click="rotate(90)">
                <v-icon small>mdi-rotate-right</v-icon>
              </v-btn>
              <v-btn icon small @click="resetCropper">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="skipCrop"> Upload Original </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelCrop">Cancel</v-btn>
          <v-btn color="primary" @click="applyCrop" :loading="uploading">
            <v-icon left>mdi-check</v-icon>
            Crop & Upload
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Upload Progress -->
    <v-dialog v-model="uploadProgressDialog" persistent max-width="400">
      <v-card>
        <v-card-title class="text-subtitle-1">
          <v-icon left>mdi-upload</v-icon>
          Uploading Photos
        </v-card-title>
        <v-card-text>
          <p class="mb-2">{{ uploadStatusText }}</p>
          <v-progress-linear
            :value="uploadProgress"
            color="primary"
            height="8"
            rounded
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import VueCropper from "vue-cropperjs";
import "cropperjs/dist/cropper.css";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import axios from "axios";

export default {
  name: "ItemPhotoManager",
  components: { VueCropper },
  props: {
    // Array of photo objects: [{ id, item_photo_url, photo_name, sort_order }]
    photos: {
      type: Array,
      default: () => [],
    },
    // If provided, photos will be saved to DB immediately on upload
    // If null, photos are only added to the local array (for new item creation)
    itemId: {
      type: [Number, String],
      default: null,
    },
    // Hides the upload and delete controls (e.g. user lacks items.edit_profile)
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      uploading: false,
      deletingPhotoId: null,
      cropperDialog: false,
      cropperImageSrc: "",
      filesToProcess: [],
      currentFileIndex: 0,
      aspectRatioIndex: 0,
      selectedAspectRatio: NaN,
      aspectRatios: [
        { label: "Free", value: NaN },
        { label: "1:1", value: 1 },
        { label: "4:3", value: 4 / 3 },
        { label: "16:9", value: 16 / 9 },
      ],
      uploadProgressDialog: false,
      uploadProgress: 0,
      uploadStatusText: "",
    };
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    onFilesSelected(e) {
      const files = Array.from(e.target.files);
      if (!files.length) return;

      // Validate files
      const validFiles = [];
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) {
          this.$alert({
            title: "File Too Large",
            message: `"${file.name}" exceeds 5MB limit.`,
            color: "warning",
            icon: "mdi-alert",
          });
          continue;
        }
        validFiles.push(file);
      }

      if (!validFiles.length) {
        this.$refs.fileInput.value = "";
        return;
      }

      // Start cropping workflow
      this.filesToProcess = validFiles;
      this.currentFileIndex = 0;
      this.loadFileForCropping(validFiles[0]);

      // Reset file input
      this.$refs.fileInput.value = "";
    },

    loadFileForCropping(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.cropperImageSrc = e.target.result;
        this.cropperDialog = true;
        this.aspectRatioIndex = 0;
        this.selectedAspectRatio = NaN;
      };
      reader.readAsDataURL(file);
    },

    setAspectRatio(ratio) {
      this.selectedAspectRatio = ratio;
      if (this.$refs.cropper) {
        this.$refs.cropper.setAspectRatio(ratio);
      }
    },

    rotate(degrees) {
      if (this.$refs.cropper) {
        this.$refs.cropper.rotate(degrees);
      }
    },

    resetCropper() {
      if (this.$refs.cropper) {
        this.$refs.cropper.reset();
      }
    },

    async applyCrop() {
      const canvas = this.$refs.cropper.getCroppedCanvas({
        maxWidth: 1200,
        maxHeight: 1200,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
      });

      canvas.toBlob(
        async (blob) => {
          const file = this.filesToProcess[this.currentFileIndex];
          const croppedFile = new File([blob], file.name, {
            type: "image/jpeg",
            lastModified: Date.now(),
          });
          await this.uploadFile(croppedFile);
          this.processNextFile();
        },
        "image/jpeg",
        0.9
      );
    },

    async skipCrop() {
      const file = this.filesToProcess[this.currentFileIndex];
      await this.uploadFile(file);
      this.processNextFile();
    },

    cancelCrop() {
      this.cropperDialog = false;
      this.filesToProcess = [];
      this.currentFileIndex = 0;
    },

    processNextFile() {
      this.currentFileIndex++;
      if (this.currentFileIndex < this.filesToProcess.length) {
        this.loadFileForCropping(this.filesToProcess[this.currentFileIndex]);
      } else {
        this.cropperDialog = false;
        this.filesToProcess = [];
        this.currentFileIndex = 0;
      }
    },

    async uploadFile(file) {
      this.uploading = true;
      this.uploadStatusText = `Uploading "${file.name}"...`;

      try {
        const storage = getStorage();
        const fileName =
          "ItemPhotos/" +
          Date.now() +
          "_" +
          Math.random().toString(36).substr(2, 9) +
          "_" +
          file.name;
        const fileRef = storageRef(storage, fileName);

        await uploadBytes(fileRef, file);
        const downloadURL = await getDownloadURL(fileRef);

        const photoData = {
          item_photo_url: downloadURL,
          photo_name: file.name,
        };

        if (this.itemId) {
          // Save to DB immediately
          const request = {
            item_id: this.itemId,
            item_photo_url: downloadURL,
            photo_name: file.name,
          };
          const response = await axios.post("/item_photo", request);
          photoData.id = response.data.id;
          photoData.sort_order = response.data.sort_order;
        }

        this.$emit("photo-added", photoData);
      } catch (err) {
        console.error("Upload failed:", err);
        this.$alert({
          title: "Upload Failed",
          message:
            "Failed to upload photo: " + (err.message || "Unknown error"),
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.uploading = false;
      }
    },

    async confirmDeletePhoto(photo, index) {
      const confirmed = await this.$confirm({
        title: "Remove Photo",
        message: "Are you sure you want to remove this photo?",
        color: "red",
        icon: "mdi-delete",
      });

      if (!confirmed) return;

      this.deletingPhotoId = photo.id || index;

      try {
        // If photo has an ID and we have an item ID, delete from DB
        if (photo.id && this.itemId) {
          await axios.delete("/item_photo/single/" + photo.id);
        }

        this.$emit("photo-removed", { photo, index });
      } catch (err) {
        console.error("Delete failed:", err);
        this.$alert({
          title: "Delete Failed",
          message:
            "Failed to delete photo: " +
            (err.response?.data?.error || err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.deletingPhotoId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.photo-card {
  position: relative;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.02);
  }

  &:hover .photo-overlay {
    opacity: 1;
  }
}

.photo-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  opacity: 0;
  transition: opacity 0.2s;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, transparent 100%);
}

.photo-action-btn {
  background: rgba(255, 255, 255, 0.9) !important;
}

.cropper-container {
  background: #f5f5f5;
  border-radius: 4px;
}
</style>
